/**
 * Versandart-Wahl: Pack-to-Bin → zulässige Versandarten → Regeln (Kunde, dann Logiful intern) → Optimierung.
 *
 * Grundsätze
 * - Jede Entscheidung ist begründet (explanation). Begründungen aus internen Regeln sind nur für Logiful sichtbar.
 * - Interne Regeln greifen NACH den Kundenregeln und können sie übersteuern (z. B. günstigere Versandart).
 * - Was der Kunde sieht, ist je Kunde einstellbar: seine eigene Wahl oder die finale Versandart (nie das "Warum").
 * - Eine Versandart ist nur wählbar, wenn JEDES Paket ihre Grenzen einhält (Gewicht, Maße, Gurtmaß, Land, Gefahrgut).
 */
import { z } from "zod";
import { packOrder, type Dims, type PackedPackage, type Packaging, type PackUnit } from "./packing.js";

// ── Versandart-Profile (Logiful pflegt sie je Lager-Versandart) ──────

export const SERVICE_LEVELS = ["economy", "standard", "express"] as const;
export type ServiceLevel = (typeof SERVICE_LEVELS)[number];

const dims = z.object({ l: z.number().positive(), w: z.number().positive(), h: z.number().positive() });
export const shippingProfileSchema = z.object({
  /** Versandart-ID im Lager (FFN shippingMethodId) */
  methodId: z.string().min(1),
  name: z.string().min(1),
  /** Bezeichnung für Kunden („DHL Paket“, „DHL Kleinpaket“) statt des internen Lagernamens („DHL LOGIFUL“) */
  customerLabel: z.string().trim().max(60).default(""),
  carrier: z.string().nullable().default(null),
  service: z.enum(SERVICE_LEVELS).default("standard"),
  active: z.boolean().default(true),
  maxWeightKg: z.number().positive().optional(),
  /** maximale Außenmaße (in beliebiger Drehung) */
  maxDims: dims.optional(),
  /** Gurtmaß: längste Seite + 2 × (zweite + dritte Seite) */
  maxGirthCm: z.number().positive().optional(),
  /** erlaubte Zielländer (leer = alle) und ausgeschlossene */
  countries: z.array(z.string().length(2)).default([]),
  excludeCountries: z.array(z.string().length(2)).default([]),
  dangerousGoods: z.boolean().default(false),
  /**
   * Versandvertrag: "customer" = eigener Vertrag des Kunden (z. B. eigenes DHL-Kleinpaket).
   * Wählt eine Regel eine Versandart über den Namen, hat ein passender Kundenvertrag immer Vorrang.
   */
  contract: z.enum(["logiful", "customer"]).default("logiful"),
  /** Einkaufspreise je Paket nach Zone (Länderliste, "*" = übrige) und Gewichtsstaffel – nur Logiful sichtbar */
  prices: z.array(z.object({ countries: z.union([z.literal("*"), z.array(z.string().length(2)).min(1)]), upToKg: z.number().positive(), priceMinor: z.number().int().min(0) })).default([]),
  /** Zuschläge je Paket, z. B. Sperrgut ab 120 cm */
  surcharges: z.array(z.object({ label: z.string().min(1), overLengthCm: z.number().positive().optional(), overGirthCm: z.number().positive().optional(), overKg: z.number().positive().optional(), priceMinor: z.number().int().min(0) })).default([]),
});
export type ShippingProfile = z.infer<typeof shippingProfileSchema>;

// ── Regeln ───────────────────────────────────────────────────────────

const condition = z.discriminatedUnion("field", [
  z.object({ field: z.literal("country"), op: z.enum(["in", "not_in"]), value: z.array(z.string().length(2)).min(1) }),
  z.object({ field: z.literal("weightKg"), op: z.enum(["gt", "gte", "lt", "lte"]), value: z.number().nonnegative() }),
  z.object({ field: z.literal("longestSideCm"), op: z.enum(["gt", "gte", "lt", "lte"]), value: z.number().nonnegative() }),
  z.object({ field: z.literal("packages"), op: z.enum(["gt", "gte", "lt", "lte"]), value: z.number().int().nonnegative() }),
  z.object({ field: z.literal("orderValue"), op: z.enum(["gt", "gte", "lt", "lte"]), value: z.number().nonnegative() }),
  z.object({ field: z.literal("tag"), op: z.enum(["has", "has_not"]), value: z.string().min(1) }),
  z.object({ field: z.literal("sku"), op: z.enum(["contains_any", "contains_none"]), value: z.array(z.string()).min(1) }),
  z.object({ field: z.literal("dangerousGoods"), op: z.enum(["is", "is_not"]) }),
  z.object({ field: z.literal("fragile"), op: z.enum(["is", "is_not"]) }),
  z.object({ field: z.literal("business"), op: z.enum(["is", "is_not"]) }),
  /** im Shop gewählte Versandart (Titel), z. B. enthält "Express" */
  z.object({ field: z.literal("shopShippingMethod"), op: z.enum(["equals", "contains"]), value: z.string().min(1) }),
  z.object({ field: z.literal("packaging"), op: z.enum(["in", "not_in"]), value: z.array(z.string()).min(1) }),
  /**
   * Jedes Paket passt (in beliebiger Drehung) in L × B × H – gemessen am AUSSENmaß inkl. Verpackung,
   * zuzüglich Toleranz je Seite (Ausbeulen, Klebeband). Z. B. Kleinpaket bis 35 × 25 × 8 cm.
   */
  z.object({ field: z.literal("packageFits"), op: z.enum(["fits", "not_fits"]), l: z.number().positive(), w: z.number().positive(), h: z.number().positive(), toleranceCm: z.number().min(0).max(10).default(0) }),
  /** schwerstes Paket inkl. Verpackung */
  z.object({ field: z.literal("maxPackageWeightKg"), op: z.enum(["gt", "gte", "lt", "lte"]), value: z.number().nonnegative() }),
  /** eine für den Kunden freigegebene Versandart, deren Name den Text enthält, existiert (z. B. "Kleinpaket") */
  z.object({ field: z.literal("methodAvailable"), op: z.enum(["exists", "not_exists"]), nameContains: z.string().min(1) }),
]);
export type ShippingCondition = z.infer<typeof condition>;

const action = z.discriminatedUnion("type", [
  /** genau diese Versandart (wenn zulässig) */
  z.object({ type: z.literal("force"), methodId: z.string().min(1) }),
  /** nur unter diesen wählen */
  z.object({ type: z.literal("allowOnly"), methodIds: z.array(z.string().min(1)).min(1) }),
  z.object({ type: z.literal("exclude"), methodIds: z.array(z.string().min(1)).min(1) }),
  /** bevorzugen, sofern zulässig (vor Optimierung) */
  z.object({ type: z.literal("prefer"), methodId: z.string().min(1) }),
  /** mindestens dieser Service (Express bleibt Express) */
  z.object({ type: z.literal("requireService"), service: z.enum(SERVICE_LEVELS) }),
  /** günstigste zulässige Versandart nach Einkaufspreisen */
  z.object({ type: z.literal("optimize") }),
  z.object({ type: z.literal("usePackaging"), packagingIds: z.array(z.string().min(1)).min(1) }),
  z.object({ type: z.literal("hold"), reason: z.string().min(1) }),
  /** Versandart nach Namen: unter den für den Kunden freigegebenen, deren Name/ID den Text enthält (bei mehreren die günstigste) */
  z.object({ type: z.literal("forceByName"), nameContains: z.string().min(1) }),
  z.object({ type: z.literal("preferByName"), nameContains: z.string().min(1) }),
  z.object({ type: z.literal("excludeByName"), nameContains: z.string().min(1) }),
]);
export type ShippingAction = z.infer<typeof action>;

export const shippingRuleSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  enabled: z.boolean().default(true),
  /** "internal" = Logiful: Wirkung und Begründung für den Kunden unsichtbar */
  visibility: z.enum(["customer", "internal"]).default("customer"),
  when: z.array(condition).default([]),
  /** Ausnahmen: trifft EINE zu, greift die Regel nicht (z. B. Ziel Schweiz, Warenwert > 500 €) */
  unless: z.array(condition).default([]),
  then: z.array(action).min(1),
});
export type ShippingRule = z.infer<typeof shippingRuleSchema>;

export const shippingSettingsSchema = z.object({
  /** was der Kunde als Versandart sieht, wenn Logiful intern ändert */
  customerSees: z.enum(["own_choice", "final_method"]).default("own_choice"),
  /** ohne passende Regel: günstigste zulässige wählen (intern, unsichtbar) */
  optimizeByDefault: z.boolean().default(false),
  /** Zuordnung Shop-Versandart → Service, z. B. Titel enthält "Express" */
  expressKeywords: z.array(z.string().min(1)).default(["express", "next day", "overnight"]),
});
export type ShippingSettings = z.infer<typeof shippingSettingsSchema>;

/**
 * Zuordnung Shop-Versandart → Lager-Versandart je Shop.
 * fixed = gilt immer (Regeln/Optimierung ändern nichts) · preferred = Startwert, Regeln und Optimierung dürfen ändern.
 */
export const shopMethodMappingSchema = z.object({
  mappings: z
    .array(z.object({ shopMethod: z.string().trim().min(1).max(200), methodId: z.string().trim().min(1).max(60), mode: z.enum(["fixed", "preferred"]).default("preferred") }))
    .max(100)
    .default([]),
  /** für nicht zugeordnete Shop-Versandarten (Vorschlag); leer = Regeln/Standard */
  fallbackMethodId: z.string().trim().min(1).max(60).nullable().default(null),
});
export type ShopMethodMapping = z.infer<typeof shopMethodMappingSchema>;

/** Passende Zuordnung (Groß-/Kleinschreibung egal, exakter Name), sonst Standard als Vorschlag */
export function matchShopMethod(mapping: unknown, shopMethod: string | null | undefined): { methodId: string; mode: "fixed" | "preferred"; source: "mapping" | "fallback" } | null {
  const m = shopMethodMappingSchema.safeParse(mapping ?? {});
  if (!m.success) return null;
  const name = (shopMethod ?? "").trim().toLowerCase();
  const hit = name ? m.data.mappings.find((x) => x.shopMethod.trim().toLowerCase() === name) : undefined;
  if (hit) return { methodId: hit.methodId, mode: hit.mode, source: "mapping" };
  return m.data.fallbackMethodId ? { methodId: m.data.fallbackMethodId, mode: "preferred", source: "fallback" } : null;
}

// ── Eingabe / Ergebnis ───────────────────────────────────────────────

export interface ShipmentInput {
  country: string;
  orderValueMinor: number;
  tags: string[];
  business: boolean;
  shopShippingMethod: string | null;
  /** bereits durch Auftragsregeln/Formular gesetzte Versandart */
  requestedMethodId: string | null;
  /** fest zugeordnete Versandart (Shop-Versandart-Zuordnung „fest“) – gilt ohne Regeln/Optimierung */
  fixedMethodId?: string | null;
  units: (PackUnit & { dangerousGoods?: boolean })[];
}

export interface Explanation {
  text: string;
  visibility: "customer" | "internal";
}

export interface ShippingDecision {
  methodId: string | null;
  /** was der Kunde als Versandart sieht (abhängig von settings.customerSees) */
  customerMethodId: string | null;
  /** Ergebnis nur aus Kundenebene – Abweichung davon = Logiful-Optimierung */
  customerLayerMethodId: string | null;
  packages: PackedPackage[];
  /** Einkaufspreis gesamt (Pakete + Zuschläge + Verpackung), falls Preise gepflegt */
  costMinor: number | null;
  hold: string[];
  /** Herkunft je Sperre: interne Sperren zeigt das Portal dem Kunden nur als "In Prüfung bei Logiful" */
  holdDetails: { reason: string; visibility: "customer" | "internal" }[];
  explanation: Explanation[];
  appliedRules: { id: string; name: string; visibility: "customer" | "internal" }[];
  /** zulässige Alternativen mit Kosten (für Logiful-Ansicht und Simulation) */
  alternatives: { methodId: string; costMinor: number | null; reason?: string }[];
  missingData: string[];
}

// ── Hilfen ───────────────────────────────────────────────────────────

const sortedSides = (d: Dims) => [d.l, d.w, d.h].sort((a, b) => b - a) as [number, number, number];
export const girth = (d: Dims) => { const [a, b, c] = sortedSides(d); return a + 2 * (b + c); };
const cmp = (op: "gt" | "gte" | "lt" | "lte", a: number, b: number) => (op === "gt" ? a > b : op === "gte" ? a >= b : op === "lt" ? a < b : a <= b);
const RANK: Record<ServiceLevel, number> = { economy: 0, standard: 1, express: 2 };
const fmtKg = (kg: number) => `${kg.toLocaleString("de-DE", { maximumFractionDigits: 2 })} kg`;
const fmtDims = (d: Dims) => sortedSides(d).map((x) => Math.round(x)).join("×") + " cm";

/** Warum passt eine Versandart nicht? (undefined = passt) */
export function profileRejection(p: ShippingProfile, packages: PackedPackage[], country: string, dangerous: boolean): string | undefined {
  if (!p.active) return "inaktiv";
  if (p.countries.length && !p.countries.includes(country)) return `nicht nach ${country}`;
  if (p.excludeCountries.includes(country)) return `nicht nach ${country}`;
  if (dangerous && !p.dangerousGoods) return "kein Gefahrgut";
  for (const pk of packages) {
    if (p.maxWeightKg != null && pk.weightKg > p.maxWeightKg) return `Paket ${fmtKg(pk.weightKg)} > max. ${fmtKg(p.maxWeightKg)}`;
    if (p.maxDims) {
      const a = sortedSides(pk.outer);
      const b = sortedSides(p.maxDims);
      if (a[0] > b[0] || a[1] > b[1] || a[2] > b[2]) return `Maße ${fmtDims(pk.outer)} > max. ${fmtDims(p.maxDims)}`;
    }
    if (p.maxGirthCm != null && girth(pk.outer) > p.maxGirthCm) return `Gurtmaß ${Math.round(girth(pk.outer))} cm > max. ${p.maxGirthCm} cm`;
  }
  return undefined;
}

/** Einkaufspreis je Paket nach Zone/Gewicht + Zuschläge; null = keine Preise gepflegt */
export function profileCost(p: ShippingProfile, packages: PackedPackage[], country: string): number | null {
  if (!p.prices.length) return null;
  let total = 0;
  for (const pk of packages) {
    const zone = p.prices.filter((r) => r.countries !== "*" && r.countries.includes(country));
    const rows = (zone.length ? zone : p.prices.filter((r) => r.countries === "*")).sort((a, b) => a.upToKg - b.upToKg);
    const row = rows.find((r) => pk.weightKg <= r.upToKg);
    if (!row) return null; // schwerer als jede Staffel
    total += row.priceMinor;
    for (const s of p.surcharges) {
      const [longest] = sortedSides(pk.outer);
      if ((s.overLengthCm != null && longest > s.overLengthCm) || (s.overGirthCm != null && girth(pk.outer) > s.overGirthCm) || (s.overKg != null && pk.weightKg > s.overKg)) total += s.priceMinor;
    }
    total += pk.packaging.costMinor;
  }
  return total;
}

function conditionMatches(c: ShippingCondition, f: Facts): boolean {
  switch (c.field) {
    case "country": return c.value.includes(f.country) === (c.op === "in");
    case "weightKg": return cmp(c.op, f.weightKg, c.value);
    case "longestSideCm": return cmp(c.op, f.longestSideCm, c.value);
    case "packages": return cmp(c.op, f.packages, c.value);
    case "orderValue": return cmp(c.op, f.orderValueMinor / 100, c.value);
    case "tag": return f.tags.includes(c.value.toLowerCase()) === (c.op === "has");
    case "sku": { const any = c.value.some((s) => f.skus.includes(s)); return c.op === "contains_any" ? any : !any; }
    case "dangerousGoods": return f.dangerous === (c.op === "is");
    case "fragile": return f.fragile === (c.op === "is");
    case "business": return f.business === (c.op === "is");
    case "shopShippingMethod": { const s = (f.shopShippingMethod ?? "").toLowerCase(); const v = c.value.toLowerCase(); return c.op === "equals" ? s === v : s.includes(v); }
    case "packaging": return f.packagingIds.some((id) => c.value.includes(id)) === (c.op === "in");
    case "packageFits": {
      const limit = sortedSides({ l: c.l, w: c.w, h: c.h });
      const fits = f.packageOuters.length > 0 && f.packageOuters.every((o) => sortedSides({ l: o.l + c.toleranceCm, w: o.w + c.toleranceCm, h: o.h + c.toleranceCm }).every((x, i) => x <= limit[i]!));
      return fits === (c.op === "fits");
    }
    case "maxPackageWeightKg": return cmp(c.op, f.maxPackageWeightKg, c.value);
    case "methodAvailable": { const n = c.nameContains.toLowerCase(); const has = f.methodNames.some((m) => m.text.includes(n)); return has === (c.op === "exists"); }
  }
}

interface Facts {
  country: string;
  weightKg: number;
  maxPackageWeightKg: number;
  packageOuters: Dims[];
  /** Namen/IDs der für den Kunden zulässigen Versandarten (klein geschrieben) */
  methodNames: { id: string; text: string }[];
  longestSideCm: number;
  packages: number;
  orderValueMinor: number;
  tags: string[];
  skus: string[];
  dangerous: boolean;
  fragile: boolean;
  business: boolean;
  shopShippingMethod: string | null;
  packagingIds: string[];
}

interface LayerState {
  allowed: Set<string>;
  forced: string | null;
  /** Vorgabe nach Namen: eine aus dieser Menge (die günstigste) */
  forcedSet: string[] | null;
  preferred: string | null;
  preferredSet: string[] | null;
  minService: ServiceLevel;
  optimize: boolean;
  holds: { reason: string; visibility: "customer" | "internal" }[];
  packagingIds: string[] | null;
}

// ── Auswertung ───────────────────────────────────────────────────────

export function decideShipping(
  input: ShipmentInput,
  ctx: { profiles: ShippingProfile[]; packagings: Packaging[]; rules: ShippingRule[]; settings: ShippingSettings },
): ShippingDecision {
  const explanation: Explanation[] = [];
  const say = (text: string, visibility: Explanation["visibility"] = "internal") => explanation.push({ text, visibility });
  // Feste Zuordnung der Shop-Versandart: gilt immer
  if (input.fixedMethodId) {
    const why = `Shop-Versandart „${input.shopShippingMethod ?? ""}“ fest zugeordnet → ${input.fixedMethodId}`;
    return { methodId: input.fixedMethodId, customerMethodId: input.fixedMethodId, customerLayerMethodId: input.fixedMethodId, packages: [], costMinor: null, hold: [], holdDetails: [], explanation: [{ text: why, visibility: "customer" }], appliedRules: [], alternatives: [], missingData: [] };
  }
  // Ohne gepflegte Profile ist die Versandart-Wahl nicht aktiv: nichts ändern, nichts sperren
  if (!ctx.profiles.some((p) => p.active)) {
    return { methodId: input.requestedMethodId, customerMethodId: input.requestedMethodId, customerLayerMethodId: input.requestedMethodId, packages: [], costMinor: null, hold: [], holdDetails: [], explanation: [], appliedRules: [], alternatives: [], missingData: [] };
  }
  const appliedRules: ShippingDecision["appliedRules"] = [];
  const settings = shippingSettingsSchema.parse(ctx.settings ?? {});
  const rules = ctx.rules.filter((r) => r.enabled);
  const customerRules = rules.filter((r) => r.visibility === "customer");
  const internalRules = rules.filter((r) => r.visibility === "internal");

  const dangerous = input.units.some((u) => u.dangerousGoods);
  const shopMethod = (input.shopShippingMethod ?? "").toLowerCase();
  const expressWanted = settings.expressKeywords.some((k) => shopMethod.includes(k.toLowerCase()));

  // Verpackung kann durch eine Regel vorgegeben werden → erst Regeln mit usePackaging vorab prüfen (auf Basis Rohdaten)
  const pack = (ids: string[] | null) => packOrder(input.units, ids ? ctx.packagings.filter((p) => ids.includes(p.id)) : ctx.packagings);
  let packing = pack(null);
  const facts = (pk: PackedPackage[]): Facts => ({
    country: input.country.toUpperCase(),
    weightKg: Math.round(pk.reduce((s, p) => s + p.weightKg, 0) * 1000) / 1000 || input.units.reduce((s, u) => s + (u.weightKg ?? 0), 0),
    longestSideCm: Math.max(0, ...pk.map((p) => sortedSides(p.outer)[0])),
    packages: pk.length,
    orderValueMinor: input.orderValueMinor,
    tags: input.tags.map((t) => t.toLowerCase()),
    skus: [...new Set(input.units.map((u) => u.sku))],
    dangerous,
    fragile: input.units.some((u) => u.fragile),
    business: input.business,
    shopShippingMethod: input.shopShippingMethod,
    packagingIds: pk.map((p) => p.packaging.id),
    maxPackageWeightKg: Math.max(0, ...pk.map((p) => p.weightKg)),
    packageOuters: pk.map((p) => p.outer),
    methodNames: ctx.profiles.filter((p) => p.active).map((p) => ({ id: p.methodId, text: `${p.name} ${p.methodId}`.toLowerCase() })),
  });

  /** freigegebene Versandarten, deren Name oder ID den Text enthält (Groß-/Kleinschreibung egal) */
  /**
   * Freigegebene Versandarten, deren Name oder ID den Text enthält (Groß-/Kleinschreibung egal).
   * Hat der Kunde passende eigene Verträge, zählen nur diese – sie müssen genutzt werden.
   */
  const byName = (text: string) => {
    const n = text.toLowerCase();
    const hits = ctx.profiles.filter((p) => p.active && `${p.name} ${p.methodId}`.toLowerCase().includes(n));
    const own = hits.filter((p) => p.contract === "customer");
    return (own.length ? own : hits).map((p) => p.methodId);
  };
  const ruleApplies = (r: ShippingRule, f: Facts) => r.when.every((c) => conditionMatches(c, f)) && !(r.unless ?? []).some((c) => conditionMatches(c, f));

  const run = (layerRules: ShippingRule[], state: LayerState, f: Facts) => {
    for (const r of layerRules) {
      if (!r.when.every((c) => conditionMatches(c, f))) continue;
      const exception = (r.unless ?? []).find((c) => conditionMatches(c, f));
      if (exception) { say(`Regel „${r.name}“ nicht angewendet: Ausnahme trifft zu`, r.visibility); continue; }
      appliedRules.push({ id: r.id, name: r.name, visibility: r.visibility });
      for (const a of r.then) {
        switch (a.type) {
          case "force": state.forced = a.methodId; state.forcedSet = null; say(`Regel „${r.name}“: Versandart ${a.methodId} vorgegeben`, r.visibility); break;
          case "allowOnly": for (const id of [...state.allowed]) if (!a.methodIds.includes(id)) state.allowed.delete(id); say(`Regel „${r.name}“: nur ${a.methodIds.join(", ")}`, r.visibility); break;
          case "exclude": for (const id of a.methodIds) state.allowed.delete(id); say(`Regel „${r.name}“: ${a.methodIds.join(", ")} ausgeschlossen`, r.visibility); break;
          case "prefer": state.preferred = a.methodId; state.preferredSet = null; say(`Regel „${r.name}“: ${a.methodId} bevorzugt`, r.visibility); break;
          case "requireService": if (RANK[a.service] > RANK[state.minService]) state.minService = a.service; say(`Regel „${r.name}“: mindestens ${a.service}`, r.visibility); break;
          case "optimize": state.optimize = true; say(`Regel „${r.name}“: günstigste zulässige Versandart`, r.visibility); break;
          case "usePackaging": state.packagingIds = a.packagingIds; say(`Regel „${r.name}“: Verpackung ${a.packagingIds.join(", ")}`, r.visibility); break;
          case "forceByName": { const ids = byName(a.nameContains); if (ids.length) { state.forcedSet = ids; state.forced = null; } const own = ids.some((id) => ctx.profiles.find((p) => p.methodId === id)?.contract === "customer"); say(ids.length ? `Regel „${r.name}“: Versandart „…${a.nameContains}…“ (${ids.join(", ")}${own ? ", eigener Vertrag des Kunden" : ""})` : `Regel „${r.name}“: keine freigegebene Versandart enthält „${a.nameContains}“`, r.visibility); break; }
          case "preferByName": { const ids = byName(a.nameContains); if (ids[0]) state.preferredSet = ids; say(`Regel „${r.name}“: „…${a.nameContains}…“ bevorzugt`, r.visibility); break; }
          case "excludeByName": { for (const id of byName(a.nameContains)) state.allowed.delete(id); say(`Regel „${r.name}“: „…${a.nameContains}…“ ausgeschlossen`, r.visibility); break; }
          case "hold": state.holds.push({ reason: a.reason, visibility: r.visibility }); say(`Regel „${r.name}“: zurückgehalten – ${a.reason}`, r.visibility); break;
        }
      }
    }
  };

  // Vorlauf: Verpackungsvorgaben aus Regeln (beide Ebenen) anwenden
  const probe: LayerState = { allowed: new Set(), forced: null, forcedSet: null, preferred: null, preferredSet: null, minService: "economy", optimize: false, holds: [], packagingIds: null };
  for (const r of rules) if (r.then.some((a) => a.type === "usePackaging") && ruleApplies(r, facts(packing.packages))) probe.packagingIds = (r.then.find((a) => a.type === "usePackaging") as { packagingIds: string[] }).packagingIds;
  if (probe.packagingIds) packing = pack(probe.packagingIds);

  const pk = packing.packages;
  const f = facts(pk);
  if (pk.length) say(`${pk.length === 1 ? "1 Paket" : `${pk.length} Pakete`}: ${pk.map((p) => `${p.packaging.name} (${fmtDims(p.outer)}, ${fmtKg(p.weightKg)})`).join(" + ")}`);
  if (packing.missingData.length) say(`Ohne Maße/Gewicht: ${packing.missingData.join(", ")} – Auswahl nur eingeschränkt möglich`);
  if (packing.unpackable.length) say(`Passt in keine Verpackung: ${packing.unpackable.join(", ")}`);

  // Zulässige Versandarten (technische Grenzen)
  const feasible = new Map<string, ShippingProfile>();
  const alternatives: ShippingDecision["alternatives"] = [];
  for (const p of ctx.profiles) {
    const why = pk.length ? profileRejection(p, pk, f.country, dangerous) : !p.active ? "inaktiv" : undefined;
    alternatives.push({ methodId: p.methodId, costMinor: why ? null : profileCost(p, pk, f.country), ...(why ? { reason: why } : {}) });
    if (!why) feasible.set(p.methodId, p);
  }
  for (const a of alternatives) if (a.reason && a.reason !== "inaktiv") say(`${a.methodId} nicht möglich: ${a.reason}`);

  const base = (): LayerState => ({ allowed: new Set(feasible.keys()), forced: null, forcedSet: null, preferred: null, preferredSet: null, minService: expressWanted ? "express" : "economy", optimize: false, holds: [], packagingIds: null });
  if (expressWanted) say(`Im Shop „${input.shopShippingMethod}“ gewählt → Express-Service`, "customer");

  const choose = (s: LayerState, requested: string | null, layer: "customer" | "full"): { id: string | null; why: string } => {
    const allowed = [...s.allowed].map((id) => feasible.get(id)!).filter((p) => RANK[p.service] >= RANK[s.minService]);
    const isAllowed = (id: string | null) => !!id && allowed.some((p) => p.methodId === id);
    if (s.forced) {
      if (isAllowed(s.forced)) return { id: s.forced, why: "vorgegeben" };
      say(`Vorgegebene Versandart ${s.forced} ist für diese Sendung nicht zulässig`, layer === "customer" ? "customer" : "internal");
    }
    const priced = allowed.map((p) => ({ p, cost: profileCost(p, pk, f.country) })).sort((a, b) => (a.cost ?? Number.MAX_SAFE_INTEGER) - (b.cost ?? Number.MAX_SAFE_INTEGER) || RANK[a.p.service] - RANK[b.p.service]);
    const cheapestOf = (ids: string[]) => priced.find((x) => ids.includes(x.p.methodId))?.p.methodId ?? null;
    if (s.forcedSet) {
      const hit = cheapestOf(s.forcedSet);
      if (hit) return { id: hit, why: "vorgegeben (Name)" };
      say(`Vorgegebene Versandart (${s.forcedSet.join(", ")}) ist für diese Sendung nicht zulässig`, layer === "customer" ? "customer" : "internal");
    }
    const cheapest = priced.find((x) => x.cost != null)?.p.methodId ?? null;
    if (s.optimize && cheapest) return { id: cheapest, why: "günstigste zulässige" };
    if (isAllowed(s.preferred)) return { id: s.preferred, why: "bevorzugt" };
    if (s.preferredSet) { const hit = cheapestOf(s.preferredSet); if (hit) return { id: hit, why: "bevorzugt (Name)" }; }
    if (isAllowed(requested)) return { id: requested, why: "gewünscht" };
    if (layer === "full" && settings.optimizeByDefault && cheapest) return { id: cheapest, why: "günstigste zulässige (Standard)" };
    if (cheapest) return { id: cheapest, why: "günstigste zulässige" };
    return { id: allowed[0]?.methodId ?? null, why: "erste zulässige" };
  };

  // Ebene 1: Kundenregeln
  const customer = base();
  run(customerRules, customer, f);
  const customerChoice = choose(customer, input.requestedMethodId, "customer");

  // Ebene 2: interne Regeln auf dem Stand der Kundenebene (können übersteuern)
  const full: LayerState = { ...customer, allowed: new Set(customer.allowed), holds: [...customer.holds] };
  const before = explanation.length;
  run(internalRules, full, f);
  const internalTouched = explanation.length > before || settings.optimizeByDefault;
  const finalChoice = internalTouched ? choose(full, customerChoice.id ?? input.requestedMethodId, "full") : customerChoice;

  if (finalChoice.id) {
    const cost = profileCost(feasible.get(finalChoice.id)!, pk, f.country);
    say(`Versandart: ${finalChoice.id} (${finalChoice.why}${cost != null ? `, ${(cost / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}` : ""})`);
    if (customerChoice.id && customerChoice.id !== finalChoice.id) say(`Intern geändert: ${customerChoice.id} → ${finalChoice.id}`);
  } else {
    say("Keine zulässige Versandart gefunden");
  }

  const holdDetails = [...full.holds];
  if (!finalChoice.id && pk.length) holdDetails.push({ reason: "Keine passende Versandart – bitte prüfen", visibility: "internal" });
  if (packing.unpackable.length) holdDetails.push({ reason: `Passt in keine Verpackung: ${packing.unpackable.join(", ")}`, visibility: "internal" });
  const hold = holdDetails.map((h) => h.reason);

  const costMinor = finalChoice.id ? profileCost(feasible.get(finalChoice.id)!, pk, f.country) : null;
  return {
    methodId: finalChoice.id,
    customerMethodId: settings.customerSees === "final_method" ? finalChoice.id : customerChoice.id,
    customerLayerMethodId: customerChoice.id,
    packages: pk,
    costMinor,
    hold,
    holdDetails,
    explanation,
    appliedRules,
    alternatives,
    missingData: packing.missingData,
  };
}

/** Für den Kunden: nur Begründungen aus seiner Ebene */
export const customerExplanation = (d: ShippingDecision) => d.explanation.filter((e) => e.visibility === "customer").map((e) => e.text);
